import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Save, Upload, Building2, X, Check } from "lucide-react";
import { adminAxios, absoluteUrl, formatApiError } from "../../lib/adminApi";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function AdminClients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(null); // { url, name }
  const fileRef = useRef();

  const load = async () => {
    try {
      const { data } = await adminAxios.get("/admin/clients");
      setItems(data.clients || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Step 1: pick file → upload + auto-process → preview the result
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminAxios.post("/admin/upload/client-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Suggest a name from the filename (without extension)
      const suggested = (file.name || "")
        .replace(/\.[^.]+$/, "")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      setPending({ url: data.url, name: suggested });
      toast.success("Logo cleaned & cropped — give it a name to add");
    } catch (err) {
      toast.error(formatApiError(err) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Step 2: confirm with a name → save to clients collection
  const confirmAdd = async () => {
    if (!pending) return;
    if (!pending.name.trim()) {
      toast.error("Enter a name for the client");
      return;
    }
    setSaving(true);
    try {
      await adminAxios.post("/admin/clients", { name: pending.name.trim(), logo_url: pending.url });
      toast.success("Client added");
      setPending(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const cancelPending = () => setPending(null);

  const updateName = async (id, name) => {
    try {
      await adminAxios.put(`/admin/clients/${id}`, { name });
      toast.success("Saved");
      setItems((arr) => arr.map((it) => (it.id === id ? { ...it, name } : it)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this client logo?")) return;
    try {
      await adminAxios.delete(`/admin/clients/${id}`);
      toast.success("Removed");
      setItems((arr) => arr.filter((it) => it.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-clients-page" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Our Clients</h1>
        <p className="text-slate-500 text-sm mt-1">Upload a logo (any image) — we auto-clean the background, tight-crop and convert to PNG. Then give it a name and add.</p>
      </div>

      {/* Upload zone */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 card-soft" data-testid="client-upload-zone">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={onFile}
          data-testid="client-file-input"
          className="hidden"
        />

        {!pending ? (
          <div
            onClick={() => !uploading && fileRef.current?.click()}
            data-testid="client-upload-dropzone"
            className={`border-2 border-dashed rounded-2xl p-8 md:p-10 text-center transition-colors cursor-pointer ${
              uploading ? "border-[#1C6B8A] bg-[#E8F4F8]" : "border-slate-200 hover:border-[#1C6B8A] hover:bg-slate-50"
            }`}
          >
            <div className="w-12 h-12 rounded-full bg-[#E8F4F8] text-[#1C6B8A] flex items-center justify-center mx-auto mb-3">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
            </div>
            <div className="font-display font-semibold text-slate-900">
              {uploading ? "Processing logo..." : "Click to upload a client logo"}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              JPG, PNG, WebP up to 10 MB · auto-cropped & background-removed
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-[200px_1fr_auto] gap-4 items-center" data-testid="client-pending-preview">
            <div className="aspect-[4/3] bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-center p-3">
              <img src={absoluteUrl(pending.url)} alt="Preview" className="max-h-full max-w-full object-contain" />
            </div>
            <div className="space-y-1.5 min-w-0">
              <Label>Client name</Label>
              <Input
                value={pending.name}
                onChange={(e) => setPending({ ...pending, name: e.target.value })}
                placeholder="e.g. Shriram Food Industry Limited"
                data-testid="client-pending-name"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && confirmAdd()}
              />
              <p className="text-[11px] text-slate-400">
                We pre-filled the name from the filename — feel free to edit.
              </p>
            </div>
            <div className="flex md:flex-col gap-2">
              <button
                onClick={confirmAdd}
                disabled={saving}
                data-testid="client-pending-confirm"
                className="h-10 px-5 rounded-full brand-btn text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Add client
              </button>
              <button
                onClick={cancelPending}
                disabled={saving}
                data-testid="client-pending-cancel"
                className="h-10 px-5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold inline-flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" /> Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#1C6B8A]" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-12 text-center text-slate-500">
          <Building2 className="w-8 h-8 mx-auto mb-3 text-slate-300" />
          No client logos yet. Upload your first one above.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="clients-list">
          {items.map((c) => (
            <ClientCard key={c.id} item={c} onSave={updateName} onDelete={remove} />
          ))}
        </div>
      )}
    </div>
  );
}

function ClientCard({ item, onSave, onDelete }) {
  const [name, setName] = useState(item.name);
  const changed = name !== item.name;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden card-soft" data-testid={`client-card-${item.id}`}>
      <div className="aspect-[4/3] bg-slate-50 p-4 flex items-center justify-center">
        <img src={absoluteUrl(item.logo_url)} alt={item.name} className="max-h-full max-w-full object-contain" />
      </div>
      <div className="p-3 space-y-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="text-sm" data-testid={`client-name-${item.id}`} />
        <div className="flex items-center gap-2">
          <button
            onClick={() => onSave(item.id, name)}
            disabled={!changed}
            data-testid={`client-save-${item.id}`}
            className="flex-1 h-9 rounded-full brand-btn text-xs font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" /> Save
          </button>
          <button
            onClick={() => onDelete(item.id)}
            data-testid={`client-delete-${item.id}`}
            className="h-9 w-9 rounded-full border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
